import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  Checkbox
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import axios from 'axios';
import { addWarehouseManagerRole } from '../users/roles';

const WarehouseList = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  
  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentWarehouse, setCurrentWarehouse] = useState(null);
  const [warehouseName, setWarehouseName] = useState('');
  const [isLocalWarehouse, setIsLocalWarehouse] = useState(false);
  const [addWarehouseError, setAddWarehouseError] = useState('');
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [newUserCredentials, setNewUserCredentials] = useState({
    username: '',
    password: '',
    warehouse: ''
  });

  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    try {
      setError(null);
      setRefreshing(true);
      const auth = JSON.parse(localStorage.getItem("auth")) || {};
      const { accessToken } = auth;
      
      const apiUrl = process.env.REACT_APP_BASE_API_URL;
      
      const response = await axios.get(
        `${apiUrl}/wms/api/warehouses`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: true,
          credentials: 'include'
        }
      );
      
      setWarehouses(response.data.data || []);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      const errorMessage = error.response?.data?.message || 
                         (error.code === 'ERR_NETWORK' ? 'Network error. Please check your connection.' : 'Failed to fetch warehouses.');
      setError(errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Add warehouse
  const handleAddWarehouse = async () => {
    if (!warehouseName.trim()) {
      setAddWarehouseError('Warehouse name cannot be empty');
      return;
    }

    try {
      setAddWarehouseError(''); // Clear any previous errors
      console.log('handleAddWarehouse - isLocalWarehouse value:', isLocalWarehouse);
      setRefreshing(true);
      const auth = JSON.parse(localStorage.getItem("auth")) || {};
      const { accessToken } = auth;
      
      const apiUrl = process.env.REACT_APP_BASE_API_URL;
      
      // Debug logging
      console.log('Adding warehouse with is_local:', isLocalWarehouse);
      
      // Create warehouse payload - try different formats since backend might expect strings instead of booleans
      const payload = { 
        warehouse_name: warehouseName,
        is_local: isLocalWarehouse,
      };
      
      console.log('Create payload:', JSON.stringify(payload));
      
      // Create warehouse
      const response = await axios.post(
        `${apiUrl}/wms/api/warehouses`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: true,
          credentials: 'include'
        }
      );
      
      // Check the full network request and response
      try {
        console.log('Warehouse created complete response:', JSON.stringify(response));
      } catch (e) {
        console.log('Response not stringifiable, logging response object:', response);
      }
      
      // Check the response data
      console.log('Warehouse created response:', response.data);
      
      // Create a corresponding role
      const normalizedName = warehouseName.toLowerCase().replace(/\s+/g, '_');
      const roleValue = `${normalizedName}_manager`;
      
      // After successful warehouse creation, create a corresponding role in localStorage
      const newRole = addWarehouseManagerRole(warehouseName);
      let successMessage = 'Warehouse added successfully';
      
      // Create role in the backend API
      try {
        await axios.post(
          `${apiUrl}/api/roles`,
          { 
            roleName: roleValue,
            description: `Manager role for ${warehouseName} warehouse`,
            permissions: [
              `view_${normalizedName}_orders`, 
              `edit_${normalizedName}_orders`,
              `manage_${normalizedName}_inventory`
            ]
          },
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            withCredentials: true,
            credentials: 'include'
          }
        );
        
        successMessage += ' with corresponding role';
        console.log(`Role ${roleValue} created successfully in backend`);
      } catch (roleError) {
        console.warn(`Error creating role in backend API: ${roleError.message}`);
        console.log('Role was still created locally in localStorage');
      }
      
      // Now create a default user with this role
      try {
        // Generate a secure default password (you might want to change this or make it random)
        const defaultPassword = `${normalizedName}pass123`;
        
        // Create username for the warehouse manager
        const username = `${normalizedName}manager`;
        
        const userResponse = await axios.post(
          `${apiUrl}/api/users`,
          {
            username: username,
            email: `${normalizedName}@example.com`, // Placeholder email
            password: defaultPassword,
            roles: [roleValue]
          },
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            withCredentials: true,
            credentials: 'include'
          }
        );
        
        // Save new user credentials temporarily to be shown to admin
        setNewUserCredentials({
          username,
          password: defaultPassword,
          warehouse: warehouseName
        });
        
        successMessage += ' and default user account';
        console.log(`Default user created for role ${roleValue}`);
      } catch (userError) {
        console.warn(`Error creating default user: ${userError.message}`);
      }
      
      // Make sure the warehouse is available in the data provider
      try {
        // Force refresh warehouse data in the data provider
        const refreshFunction = window.forceWarehouseRefresh;
        if (typeof refreshFunction === 'function') {
          window.forceWarehouseRefresh();
          console.log('Forced refresh of warehouse data in data provider');
        }
      } catch (refreshError) {
        console.warn('Could not refresh warehouse data cache', refreshError);
      }
      
      // Show success notification
      setSuccessMsg(successMessage);
      setSnackbar({
        open: true,
        message: successMessage,
        severity: 'success'
      });
      setWarehouseName('');
      setIsLocalWarehouse(false);
      setOpenAddDialog(false);
      fetchWarehouses();
    } catch (error) {
      console.error('Error adding warehouse:', error);
      
      // Check if this is a duplicate warehouse error
      const errorMessage = error.response?.data?.message || 
                         (error.code === 'ERR_NETWORK' ? 'Network error. Please check your connection.' : 'Failed to add warehouse.');
      
      // If the error message indicates a duplicate warehouse
      if (errorMessage.toLowerCase().includes('duplicate') || 
          errorMessage.toLowerCase().includes('already exists') ||
          error.response?.status === 409) {
        // Show error in the add warehouse dialog
        setAddWarehouseError(`A warehouse with the name "${warehouseName}" already exists in the system.`);
      } else {
        // Show general error in add warehouse dialog
        setAddWarehouseError(errorMessage);
      }
      setRefreshing(false);
    } finally {
      setRefreshing(false);
    }
  };

  // Update warehouse
  const handleUpdateWarehouse = async () => {
    if (!warehouseName.trim()) {
      setSnackbar({
        open: true,
        message: 'Warehouse name cannot be empty',
        severity: 'error'
      });
      return;
    }

    try {
      setRefreshing(true);
      const auth = JSON.parse(localStorage.getItem("auth")) || {};
      const { accessToken } = auth;
      
      const apiUrl = process.env.REACT_APP_BASE_API_URL;
      const currentName = currentWarehouse.warehouse_name || currentWarehouse.name;
      
      // Debug logging
      console.log('Updating warehouse with is_local:', isLocalWarehouse);
      
      const payload = { 
        warehouse_name: warehouseName,
        is_local: isLocalWarehouse
        // Try string representations of boolean values
      };
      
      console.log('Update payload:', payload);
      
      await axios.put(
        `${apiUrl}/wms/api/warehouses/${encodeURIComponent(currentName)}`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: true,
          credentials: 'include'
        }
      );
      
      setSnackbar({
        open: true,
        message: 'Warehouse updated successfully',
        severity: 'success'
      });
      
      setOpenEditDialog(false);
      setWarehouseName('');
      setCurrentWarehouse(null);
      fetchWarehouses();
    } catch (error) {
      console.error('Error updating warehouse:', error);
      const errorMessage = error.response?.data?.message || 
                         (error.code === 'ERR_NETWORK' ? 'Network error. Please check your connection.' : 'Failed to update warehouse.');
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setRefreshing(false);
    }
  };

  // Delete warehouse
  const handleDeleteWarehouse = async () => {
    try {
      setRefreshing(true);
      const auth = JSON.parse(localStorage.getItem("auth")) || {};
      const { accessToken } = auth;
      
      const apiUrl = process.env.REACT_APP_BASE_API_URL;
      const currentName = currentWarehouse.warehouse_name || currentWarehouse.name;
      
      await axios.delete(
        `${apiUrl}/wms/api/warehouses/${encodeURIComponent(currentName)}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: true,
          credentials: 'include'
        }
      );
      
      setSnackbar({
        open: true,
        message: 'Warehouse deleted successfully',
        severity: 'success'
      });
      
      setOpenDeleteDialog(false);
      setCurrentWarehouse(null);
      fetchWarehouses();
    } catch (error) {
      console.error('Error deleting warehouse:', error);
      const errorMessage = error.response?.data?.message || 
                         (error.code === 'ERR_NETWORK' ? 'Network error. Please check your connection.' : 'Failed to delete warehouse.');
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setRefreshing(false);
    }
  };

  const openEditDialogWithWarehouse = (warehouse) => {
    setCurrentWarehouse(warehouse);
    setWarehouseName(warehouse.warehouse_name || warehouse.name);
    setIsLocalWarehouse(warehouse.is_local || false);
    setOpenEditDialog(true);
  };

  const openDeleteDialogWithWarehouse = (warehouse) => {
    setCurrentWarehouse(warehouse);
    setOpenDeleteDialog(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const filteredWarehouses = warehouses.filter(warehouse => {
    const warehouseName = warehouse.warehouse_name || warehouse.name || '';
    return warehouseName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const displayedWarehouses = filteredWarehouses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading && warehouses.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 2,
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Warehouses</Typography>
          
          <Tooltip title="Refresh warehouses">
            <IconButton 
              onClick={fetchWarehouses} 
              disabled={refreshing}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="Add new warehouse">
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                setWarehouseName('');
                setIsLocalWarehouse(false);
                setAddWarehouseError('');
                setOpenAddDialog(true);
              }}
            >
              Add Warehouse
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <TextField
        fullWidth
        label="Search by warehouse name"
        variant="outlined"
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setPage(0);
        }}
        sx={{ mb: 2 }}
      />

      {error && (
        <Box sx={{ color: 'error.main', p: 2, mb: 2 }}>
          <Typography>{error}</Typography>
        </Box>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Warehouse Name</strong></TableCell>
                <TableCell><strong>Location Type</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedWarehouses.map((warehouse) => (
                <TableRow key={warehouse.id}>
                  <TableCell>{warehouse.warehouse_name || warehouse.name}</TableCell>
                  <TableCell>{warehouse.is_local ? 'Local' : 'Remote'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit warehouse">
                      <IconButton 
                        color="primary"
                        onClick={() => openEditDialogWithWarehouse(warehouse)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete warehouse">
                      <IconButton 
                        color="error"
                        onClick={() => openDeleteDialogWithWarehouse(warehouse)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filteredWarehouses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No warehouses found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredWarehouses.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10]}
        />
      </Paper>

      {/* Add Warehouse Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
        <DialogTitle>Add New Warehouse</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter the name for the new warehouse.
          </DialogContentText>
          {addWarehouseError && (
            <Alert 
              severity="error" 
              sx={{ mt: 2, mb: 2 }}
              icon={<ErrorOutlineIcon />}
            >
              {addWarehouseError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Warehouse Name"
            type="text"
            fullWidth
            variant="outlined"
            value={warehouseName}
            onChange={(e) => setWarehouseName(e.target.value)}
            sx={{ mb: 2 }}
            error={!!addWarehouseError}
          />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={isLocalWarehouse}
              onChange={(e) => {
                const newValue = e.target.checked;
                console.log('Checkbox changed to:', newValue, 'at time:', new Date().toISOString());
                setIsLocalWarehouse(newValue);
                // Immediately after setting the state, log what it would be
                console.log('isLocalWarehouse set to:', newValue);
                // Add delay to check it after React has processed the state update
                setTimeout(() => {
                  console.log('isLocalWarehouse after timeout:', isLocalWarehouse);
                }, 100);
              }}
              id="is-local-checkbox"
            />
            <Typography component="label" htmlFor="is-local-checkbox">
              Is local warehouse? {isLocalWarehouse ? '(Yes)' : '(No)'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddWarehouse} 
            variant="contained" 
            color="primary"
            disabled={refreshing}
          >
            {refreshing ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Warehouse Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
        <DialogTitle>Edit Warehouse</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Update the warehouse details.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Warehouse Name"
            type="text"
            fullWidth
            variant="outlined"
            value={warehouseName}
            onChange={(e) => setWarehouseName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={isLocalWarehouse}
              onChange={(e) => {
                const newValue = e.target.checked;
                console.log('Edit checkbox changed to:', newValue);
                setIsLocalWarehouse(newValue);
                // Immediately after setting the state, log what it would be
                console.log('isLocalWarehouse set to:', newValue);
                // Add delay to check it after React has processed the state update
                setTimeout(() => {
                  console.log('isLocalWarehouse after timeout:', isLocalWarehouse);
                }, 100);
              }}
              id="is-local-edit-checkbox"
            />
            <Typography component="label" htmlFor="is-local-edit-checkbox">
              Is local warehouse? {isLocalWarehouse ? '(Yes)' : '(No)'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateWarehouse} variant="contained" color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Warehouse Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Warehouse</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the warehouse "{currentWarehouse ? (currentWarehouse.warehouse_name || currentWarehouse.name) : ''}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteWarehouse} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      {successMsg && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" color="textSecondary">
            {successMsg}
          </Typography>
          {newUserCredentials.username && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body1" color="textSecondary">
                Default user credentials:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Username: {newUserCredentials.username}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Password: {newUserCredentials.password}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default WarehouseList;
